/**
 * Protected Route Component
 * 
 * A higher-order component that wraps routes requiring authentication.
 * Checks if the user is authenticated and redirects to login if not.
 * Used to protect private pages from unauthorized access.
 */

import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAppSelector } from '../hooks/redux';

/**
 * Props interface for ProtectedRoute component
 */
interface ProtectedRouteProps {
  children: React.ReactNode; // The component(s) to render if authenticated
}

/**
 * ProtectedRoute Component
 * 
 * Conditionally renders child components based on authentication status.
 * If user is not authenticated, redirects to login page.
 * If user is authenticated, renders the protected content.
 * Shows loading state while authentication is being verified.
 * 
 * @param children - The components to render when user is authenticated
 * @returns JSX element containing protected content or redirect to login
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  // Get authentication status from Redux store
  const { isAuthenticated, isLoading } = useAppSelector((state) => state.auth);

  // Wait for auth initialization to complete before making routing decisions
  // This prevents redirecting during the initial auth check on page refresh
  // and prevents showing error pages briefly during login
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if user is not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Render protected content if user is authenticated
  return <>{children}</>;
};

export default ProtectedRoute;
